import React, { useEffect, useMemo, useRef, useState } from 'react';
import ListIdentity from '../components/layout/ListIdentity';
import '../components/layout/AppPage.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Table, Button, Tag, App, Input, Select, DatePicker, Row, Col, Modal, Descriptions, Divider, Dropdown, Tooltip, Segmented, Badge } from 'antd';
import type { MenuProps } from 'antd';
import { DeleteOutlined, EditOutlined, EyeOutlined, SearchOutlined, FileSearchOutlined, FilterOutlined, InboxOutlined, MoreOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useFeedback } from '../utils/feedback';
import { getPZErrorMessage, isBlockingPZError, PZ_ERROR_CODE_KEYS } from '../modules/warehouse/pzErrorMessages';
import { findBlockedAction, getBlockedActionMessage, normalizeBlockedActions } from '../modules/warehouse/blockedActions';
import { authService } from '../services/auth';
import { formatDateEuropean } from '../utils/dateUtils';
import { formatCurrency, formatCurrencyUnitPrice } from '../utils/currency';
import { formatPZDocumentTotal } from '../utils/pzTotals';
import dayjs from 'dayjs';
import { PZDocument, PZItem, PZStatus } from '../types/pz';
import ProductNameWithDimensions from '../components/products/ProductNameWithDimensions';
import {
  canDeleteWarehouseDocument,
  canManagePZDocuments,
} from '../modules/warehouse/warehouseDocumentAccessPolicy';
import { usePageAutoRefresh } from '../hooks/usePageAutoRefresh';
import WarehouseDocumentsTabs from '../modules/warehouse/components/WarehouseDocumentsTabs';
import { compareMonthlyDocumentNumbers } from '../utils/documentNumber';
import {
  PZ_OPERATIONAL_STATUSES,
  getPZOperationalStatus,
} from '../modules/warehouse/warehouseDocumentOperationalStatus';
import {
  WAREHOUSE_DOCUMENT_LIST_VIEW_PARAM,
  getCancelledDocumentKind,
  parseWarehouseDocumentListView,
  partitionWarehouseDocumentsByView,
  type WarehouseDocumentListView,
} from '../modules/warehouse/warehouseDocumentListView';

const { RangePicker } = DatePicker;
const { Option } = Select;

const PZDocuments: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation(['pz', 'common']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [pzDocuments, setPzDocuments] = useState<PZDocument[]>([]);
  // "Aktywne" vs "Anulowane i storno" lives in the URL (?view=cancelled) like
  // other list filters, so links and reloads land on the same category.
  const listView = parseWarehouseDocumentListView(searchParams.get(WAREHOUSE_DOCUMENT_LIST_VIEW_PARAM));
  const handleListViewChange = (value: WarehouseDocumentListView) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'active') {
      next.delete(WAREHOUSE_DOCUMENT_LIST_VIEW_PARAM);
    } else {
      next.set(WAREHOUSE_DOCUMENT_LIST_VIEW_PARAM, value);
    }
    setSearchParams(next);
  };
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [downloading, setDownloading] = useState<string | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<PZDocument | null>(null);
  const [statusChangingId, setStatusChangingId] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const statusChangesInFlightRef = useRef(new Set<string>());

  const currentUser = authService.getCurrentUser();
  const canEdit = canManagePZDocuments(currentUser);
  const canDelete = canDeleteWarehouseDocument(currentUser);
  const canViewPZPurchasePrices = authService.canViewWarehouseDocumentPurchasePrices();
  const canViewPurchaseDocuments = authService.canViewPurchaseDocuments();

  // Filter states
  const [filters, setFilters] = useState<{
    status: PZStatus[];
    dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
    search: string;
  }>({
    status: [],
    dateRange: null,
    search: ''
  });

  useEffect(() => {
    fetchPZDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPZDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get('/pz');
      setPzDocuments(response.data);
    } catch (error: any) {
      feedback.error(getPZErrorMessage(error, t, 'pz:error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  usePageAutoRefresh(fetchPZDocuments);

  const handleDelete = async (id: string, pzNumber: string) => {
    try {
      await api.delete(`/pz/${id}`);
      feedback.success(t('pz:success.deleted', { pzNumber }));
      fetchPZDocuments();
    } catch (error: any) {
      feedback.error(getPZErrorMessage(error, t, 'pz:error.deleteFailed', { pzNumber }), {
        sticky: isBlockingPZError(error),
      });
    }
  };

  const handleStatusUpdate = async (id: string, pzNumber: string, status: PZStatus) => {
    if (statusChangesInFlightRef.current.has(id)) return;
    statusChangesInFlightRef.current.add(id);
    setStatusChangingId(id);
    try {
      await api.put(`/pz/${id}/status`, { status });
      if (status === PZStatus.RECEIVED) {
        feedback.success(t('pz:success.received', { pzNumber }));
      } else if (status === PZStatus.CONFIRMED) {
        feedback.success(t('pz:success.confirmed', { pzNumber }));
      } else if (status === PZStatus.CANCELLED) {
        feedback.success(t('pz:success.cancelled', { pzNumber }));
      }
      await fetchPZDocuments();
    } catch (error: any) {
      // Shared mapper: the same backend code must read the same on the list as
      // it does on the document page.
      const errorKey = status === PZStatus.RECEIVED ? 'receiveFailed'
        : status === PZStatus.CONFIRMED ? 'confirmFailed'
        : status === PZStatus.CANCELLED ? 'cancelFailed' : 'saveFailed';
      feedback.error(getPZErrorMessage(error, t, `pz:error.${errorKey}`), {
        sticky: isBlockingPZError(error),
      });
    } finally {
      statusChangesInFlightRef.current.delete(id);
      setStatusChangingId((current) => current === id ? null : current);
    }
  };

  const confirmStatusUpdate = async (record: PZDocument, status: PZStatus) => {
    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('pz:confirm.receivePZ'),
        content: t('pz:confirm.receivePZDescription'),
        okText: t('pz:actions.receiveVirtual'),
        cancelText: t('common:no'),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!confirmed) return;
    await handleStatusUpdate(record.id, record.pzNumber, status);
  };

  const handleDownloadPDF = async (id: string, pzNumber: string) => {
    setDownloading(id);
    try {
      const response = await api.get(`/pz/${id}/pdf`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${pzNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      feedback.success(t('pz:success.pdfDownloaded', { pzNumber }));
    } catch (error: any) {
      feedback.error(getPZErrorMessage(error, t, 'pz:error.pdfGenerateFailed'));
    } finally {
      setDownloading(null);
    }
  };

  const handlePreviewDocument = (doc: PZDocument) => {
    setPreviewDoc(doc);
    setPreviewVisible(true);
  };

  const getStatusColor = (status: PZStatus) => {
    switch (status) {
      case PZStatus.DRAFT:
        return 'default';
      case PZStatus.CONFIRMED:
        return 'processing';
      case PZStatus.RECEIVED:
        return 'success';
      case PZStatus.CANCELLED:
        return 'error';
      default:
        return 'default';
    }
  };

  const documentsByView = useMemo(() => partitionWarehouseDocumentsByView(pzDocuments), [pzDocuments]);
  const cancelledCount = documentsByView.cancelled.length;

  // Filter documents based on current filters
  const filteredDocuments = useMemo(() => {
    let filtered = listView === 'cancelled' ? [...documentsByView.cancelled] : [...documentsByView.active];

    // Status filter (active view only — the cancelled category has one status)
    if (listView === 'active' && filters.status.length > 0) {
      filtered = filtered.filter(doc => filters.status.includes(getPZOperationalStatus(doc.status)));
    }

    // Date range filter
    if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
      const startDate = filters.dateRange[0].startOf('day');
      const endDate = filters.dateRange[1].endOf('day');
      filtered = filtered.filter(doc => {
        const receiptDate = dayjs(doc.receiptDate);
        return receiptDate.isAfter(startDate) && receiptDate.isBefore(endDate);
      });
    }

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(doc =>
        doc.pzNumber.toLowerCase().includes(searchLower) ||
        doc.supplierName?.toLowerCase().includes(searchLower) ||
        doc.supplier?.name?.toLowerCase().includes(searchLower) ||
        (canViewPurchaseDocuments && doc.supplierInvoiceNumber?.toLowerCase().includes(searchLower))
      );
    }

    return filtered;
  }, [documentsByView, listView, filters, canViewPurchaseDocuments]);

  const clearFilters = () => {
    setFilters({
      status: [],
      dateRange: null,
      search: ''
    });
  };

  const getActionMenuItems = (record: PZDocument): MenuProps['items'] => {
    const statusChanging = statusChangingId === record.id;
    const items: NonNullable<MenuProps['items']> = [
      {
        key: 'view',
        icon: <EyeOutlined />,
        label: t('common:open'),
      },
      {
        key: 'preview',
        icon: <FileSearchOutlined />,
        label: t('pz:actions.preview'),
      },
    ];

    // Backend-computed reasons (list rows carry status-only ones). A blocked
    // action stays in the menu, disabled, with the reason on hover — the
    // operator learns why without asking.
    const blockedActions = normalizeBlockedActions(record.blockedActions);
    const blockedLabel = (action: string, label: string) => {
      const blocked = findBlockedAction(blockedActions, action);
      return blocked
        ? <Tooltip title={getBlockedActionMessage(blocked, t, 'pz', PZ_ERROR_CODE_KEYS, { siblings: blockedActions })}><span>{label}</span></Tooltip>
        : label;
    };
    const isBlocked = (action: string) => Boolean(findBlockedAction(blockedActions, action));

    if (canEdit && (record.status === PZStatus.DRAFT || record.status === PZStatus.CANCELLED)) {
      items.push({
        key: 'edit',
        icon: <EditOutlined />,
        label: blockedLabel('edit', t('common:edit')),
        disabled: isBlocked('edit'),
      });
    }

    if (canEdit && record.status !== PZStatus.RECEIVED) {
      items.push({
        key: 'receive',
        icon: <InboxOutlined />,
        label: blockedLabel('receive', t('pz:actions.receiveVirtual')),
        disabled: statusChanging || isBlocked('receive'),
      });
    }

    if (canViewPZPurchasePrices) {
      items.push({
        key: 'pdf',
        icon: <FilePdfOutlined />,
        label: t('pz:actions.downloadPDF'),
      });
    }

    // Parity with WZ: only a received document (stock already affected) is
    // protected — shown disabled with the reason rather than hidden.
    if (canDelete) {
      items.push({
        key: 'delete',
        danger: true,
        icon: <DeleteOutlined />,
        label: blockedLabel('delete', t('common:delete')),
        disabled: isBlocked('delete'),
      });
    }

    return items;
  };

  const columns = [
    {
      title: t('common:actions'),
      key: 'actions',
      width: 56,
      align: 'center' as const,
      fixed: 'left' as const,
      render: (record: PZDocument) => (
        <Dropdown
          trigger={['click']}
          placement="bottomLeft"
          menu={{
            items: getActionMenuItems(record),
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();

              if (key === 'view') {
                navigate(`/app/pz/${record.id}`);
              }

              if (key === 'preview') {
                handlePreviewDocument(record);
              }

              if (key === 'edit') {
                navigate(`/app/pz/${record.id}?mode=edit`);
              }

              if (key === 'pdf') {
                void handleDownloadPDF(record.id, record.pzNumber);
              }

              if (key === 'receive') {
                void confirmStatusUpdate(record, PZStatus.RECEIVED);
              }

              if (key === 'delete') {
                modal.confirm({
                  title: t('pz:confirm.delete'),
                  okText: t('common:yes'),
                  cancelText: t('common:no'),
                  okButtonProps: { danger: true },
                  onOk: () => { void handleDelete(record.id, record.pzNumber); },
                });
              }
            },
          }}
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            aria-label={t('common:actions')}
            loading={statusChangingId === record.id}
            disabled={statusChangingId === record.id}
            onClick={(event) => event.stopPropagation()}
          />
        </Dropdown>
      ),
    },
    {
      title: t('pz:fields.pzNumber'),
      dataIndex: 'pzNumber',
      key: 'pzNumber',
      width: 196,
      sorter: (a: PZDocument, b: PZDocument, sortOrder?: 'ascend' | 'descend' | null) =>
        compareMonthlyDocumentNumbers(a.pzNumber, b.pzNumber, sortOrder),
      defaultSortOrder: 'descend' as const,
      render: (_: unknown, record: PZDocument) => {
        const operationalStatus = record.status ? getPZOperationalStatus(record.status) : null;
        const cancelledKind = getCancelledDocumentKind(record);
        const isStorno = cancelledKind === 'STORNO';
        const statusTag = isStorno ? (
          <Tag className="warehouse-storno-tag">{t('pz:storno.tag')}</Tag>
        ) : cancelledKind ? (
          <Tag color="error">{t('pz:cancelledKind.cancelled')}</Tag>
        ) : operationalStatus ? (
          <Tag color={getStatusColor(operationalStatus)}>
            {t(`pz:status.${operationalStatus.toLowerCase()}`)}
          </Tag>
        ) : null;

        return (
          <ListIdentity
            name={record.pzNumber || '-'}
            onOpen={() => navigate(`/app/pz/${record.id}`)}
            tags={statusTag}
            meta={[
              canViewPurchaseDocuments && record.supplierInvoiceNumber
                ? t('pz:list.supplierInvoice', { number: record.supplierInvoiceNumber })
                : null,
            ]}
            notes={[
              isStorno ? t('pz:storno.annotation', {
                reason: record.stornoReason || '',
                who: record.stornoByName || t('pz:storno.unknownUser'),
                when: record.stornoAt ? dayjs(record.stornoAt).format('DD.MM.YYYY HH:mm') : '',
              }) : null,
            ]}
          />
        );
      },
    },
    {
      title: t('pz:fields.receiptDate'),
      dataIndex: 'receiptDate',
      key: 'receiptDate',
      width: 162,
      render: (date: string) => <span className="app-page__num">{formatDateEuropean(date)}</span>,
      sorter: (a: PZDocument, b: PZDocument) => dayjs(a.receiptDate).unix() - dayjs(b.receiptDate).unix(),
    },
    {
      title: t('pz:fields.supplier'),
      key: 'supplier',
      width: 196,
      render: (record: PZDocument) => (
        <span className="app-page__party" title={record.supplier?.name || record.supplierName || undefined}>
          {record.supplier?.name || record.supplierName || '-'}
        </span>
      ),
    },
    // „Kwota faktury dostawcy” left the list: empty on every production PZ
    // (2026-10-19); the value stays in the preview and on the PZ card.
    {
      title: t('pz:fields.items'),
      key: 'items',
      width: 226,
      render: (record: PZDocument) => {
        if (!record.items || record.items.length === 0) return <span className="app-page__muted">-</span>;
        const itemNames = record.items.map(item => item.productName || item.sku || 'N/A');
        // First two lines, each with its dimensions beneath; the rest as „+N”.
        const shown = record.items.slice(0, 2);
        const rest = record.items.length - shown.length;
        return (
          <div className="app-page__lines" title={itemNames.join(', ')}>
            {shown.map((item, index) => (
              <ProductNameWithDimensions
                key={item.id || index}
                name={`${itemNames[index]}${index === shown.length - 1 && rest > 0 ? ` +${rest}` : ''}`}
                dimensions={item}
                dimensionsTestId="pz-list-item-dimensions"
              />
            ))}
          </div>
        );
      },
    },
    ...(canViewPZPurchasePrices ? [{
      // Pinned at the right edge with a short header so the value keeps „zł”
      // whenever the table scrolls (staging v3.13: „Wartość c” / „20 210,00”).
      title: t('pz:fields.documentValue'),
      key: 'totalValue',
      width: 130,
      align: 'right' as const,
      fixed: 'right' as const,
      render: (record: PZDocument) => (
        <span className="pz-documents-page__value app-page__num">{formatPZDocumentTotal(record)}</span>
      ),
    }] : []),
  ];
  const tableMinWidth = columns.reduce((sum, column) => sum + (typeof column.width === 'number' ? column.width : 0), 0);
  const advancedFilterCount = [
    listView === 'active' && filters.status.length > 0,
    Boolean(filters.dateRange && filters.dateRange[0] && filters.dateRange[1]),
  ].filter(Boolean).length;
  const hasAnyFilter = advancedFilterCount > 0 || Boolean(filters.search.trim());

  return (
    <div className="app-page app-page--modern pz-documents-page">
      <WarehouseDocumentsTabs active="pz" />
      <section className="app-page__card">
        <div className="app-page__toolbar">
          <Input
            className="app-page__toolbar-search"
            placeholder={t('pz:filters.search')}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            prefix={<SearchOutlined />}
            allowClear
          />
          <div className="app-page__toolbar-actions">
            <Badge count={advancedFilterCount} size="small" offset={[-2, 2]}>
              <Button
                icon={<FilterOutlined />}
                type={filtersOpen || advancedFilterCount > 0 ? 'primary' : 'default'}
                ghost={filtersOpen || advancedFilterCount > 0}
                onClick={() => setFiltersOpen((open) => !open)}
              >
                {t('pz:filters.toggle')}
              </Button>
            </Badge>
            {hasAnyFilter && (
              <Button type="link" onClick={clearFilters}>
                {t('common:clearFilters')}
              </Button>
            )}
            <Segmented<WarehouseDocumentListView>
              aria-label={t('pz:listView.label')}
              value={listView}
              onChange={handleListViewChange}
              options={[
                { value: 'active', label: t('pz:listView.active') },
                { value: 'cancelled', label: `${t('pz:listView.cancelled')} (${cancelledCount})` },
              ]}
            />
            <span className="app-page__count" aria-label={t('common:totalItems', { count: filteredDocuments.length })}>
              {filteredDocuments.length}
            </span>
          </div>
        </div>

        {filtersOpen && (
          <Row gutter={[12, 12]} className="app-page__filters">
            {listView === 'active' && (
              <Col xs={24} sm={12} md={8} lg={6}>
                <Select
                  mode="multiple"
                  aria-label={t('pz:filters.status')}
                  placeholder={t('pz:filters.status')}
                  value={filters.status}
                  onChange={(value) => setFilters({ ...filters, status: value })}
                  allowClear
                >
                  {PZ_OPERATIONAL_STATUSES.filter((status) => status !== PZStatus.CANCELLED).map(status => (
                    <Option key={status} value={status}>
                      {t(`pz:status.${status?.toLowerCase() || 'draft'}`)}
                    </Option>
                  ))}
                </Select>
              </Col>
            )}
            <Col xs={24} sm={12} md={8} lg={6}>
              <RangePicker
                value={filters.dateRange}
                onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
                format="DD.MM.YYYY"
                placeholder={[t('pz:filters.dateFrom'), t('pz:filters.dateTo')]}
              />
            </Col>
          </Row>
        )}

        <Table
          className="pz-documents-page__table app-page__list-table"
          columns={columns}
          dataSource={filteredDocuments}
          rowKey="id"
          loading={loading}
          tableLayout="fixed"
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => t('common:totalItems', { count: total }),
          }}
          // Every column has a width; the table scrolls only below their sum, so
          // at 1280 px and wider it fits its card without horizontal scroll.
          scroll={{ x: tableMinWidth }}
        />
      </section>

      <Modal
        title={t('pz:actions.documentPreview')}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        maskClosable={false}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            {t('common:close')}
          </Button>,
          ...(canViewPZPurchasePrices && previewDoc ? [
            <Button
              key="download"
              type="primary"
              icon={<FilePdfOutlined />}
              loading={downloading === previewDoc.id}
              onClick={() => handleDownloadPDF(previewDoc.id, previewDoc.pzNumber)}
            >
              {t('pz:actions.downloadPDF')}
            </Button>,
          ] : []),
        ]}
        width={800}
      >
        {previewDoc && (
          <div>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label={t('pz:fields.pzNumber')}>
                {previewDoc.pzNumber}
              </Descriptions.Item>
              <Descriptions.Item label={t('pz:fields.status')}>
                <Tag color={getStatusColor(getPZOperationalStatus(previewDoc.status))}>
                  {t(`pz:status.${getPZOperationalStatus(previewDoc.status).toLowerCase()}`)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('pz:fields.receiptDate')}>
                {formatDateEuropean(previewDoc.receiptDate)}
              </Descriptions.Item>
              <Descriptions.Item label={t('pz:fields.supplier')}>
                {previewDoc.supplier?.name || previewDoc.supplierName}
              </Descriptions.Item>
              {canViewPurchaseDocuments && (
                <Descriptions.Item label={t('pz:fields.supplierInvoiceNumber')}>
                  {previewDoc.supplierInvoiceNumber || '-'}
                </Descriptions.Item>
              )}
              {canViewPZPurchasePrices && (
                <>
                  <Descriptions.Item label={t('pz:fields.supplierInvoiceAmount')}>
                    {previewDoc.supplierInvoiceAmount
                      ? formatCurrency(previewDoc.supplierInvoiceAmount, previewDoc.supplierInvoiceCurrency || 'PLN')
                      : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('pz:fields.totalValue')}>
                    {formatPZDocumentTotal(previewDoc)}
                  </Descriptions.Item>
                </>
              )}
            </Descriptions>

            {previewDoc.items && previewDoc.items.length > 0 && (
              <>
                <Divider orientation="left">{t('pz:fields.items')}</Divider>
                <Table
                  dataSource={previewDoc.items}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  columns={[
                    {
                      title: t('pz:itemFields.productName'),
                      dataIndex: 'productName',
                      key: 'productName',
                      render: (text: string | undefined, record: PZItem) => (
                        <ProductNameWithDimensions
                          name={text || record.sku || '-'}
                          dimensions={record}
                          dimensionsTestId="pz-preview-item-dimensions"
                        />
                      )
                    },
                    {
                      title: t('pz:itemFields.sku'),
                      dataIndex: 'sku',
                      key: 'sku',
                    },
                    {
                      title: t('pz:itemFields.quantityOrdered'),
                      dataIndex: 'quantityOrdered',
                      key: 'quantityOrdered',
                    },
                    {
                      title: t('pz:itemFields.quantityReceived'),
                      dataIndex: 'quantityReceived',
                      key: 'quantityReceived',
                    },
                    ...(canViewPZPurchasePrices ? [{
                      title: t('pz:itemFields.unitPrice'),
                      dataIndex: 'unitPrice',
                      key: 'unitPrice',
                      render: (value: number | string | undefined, record: PZItem) =>
                        value === undefined ? '-' : formatCurrencyUnitPrice(Number(value), record.currency)
                    },
                    {
                      title: t('pz:itemFields.totalPrice'),
                      dataIndex: 'totalPrice',
                      key: 'totalPrice',
                      render: (value: number | string | undefined, record: PZItem) =>
                        value === undefined ? '-' : formatCurrency(Number(value), record.currency)
                    }] : []),
                  ]}
                />
              </>
            )}

            {previewDoc.notes && (
              <>
                <Divider orientation="left">{t('pz:fields.notes')}</Divider>
                <p>{previewDoc.notes}</p>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PZDocuments;
